import { Button, Input, message, Modal, Select, Typography, Form, InputNumber } from 'antd';
import { useEffect } from 'react'; // Import useEffect
import { motion } from 'framer-motion';
// axiosInstance is imported in the parent, no need to import here if it's passed down or handled by parent component

const { Title } = Typography;
const { Option } = Select;

// Renamed from OrderModal to AddProduitAttestation for clarity
const AddProduitAttestation = ({ orderModalOpen, setOrderModalOpen, categories, onSubmitCommandeAttestation, editingProduct }) => {
    const [form] = Form.useForm(); // Initialize Ant Design Form instance

    // Effect to populate form when editingProduct changes
    useEffect(() => {
        if (editingProduct) {
            // Set form fields with data from editingProduct
            form.setFieldsValue({
                typeAttestation: editingProduct.typeAttestation,
                montant: editingProduct.montant,
                // Map categories from editingProduct to an array of their IDs or codes
                // Assuming categories in editingProduct have a 'codeCategorie' or '_id'
                categories: editingProduct.categories?.map(cat => cat.codeCategorie || cat._id),
                moyenReglement: editingProduct.moyenReglement,
            });
        } else {
            form.resetFields(); // Clear form if not editing
        }
    }, [editingProduct, form]); // Depend on editingProduct and form instance

    const paymentModality = [
        {
            label: 'lors de la retrocession',
            value: 'RETROCESSION'
        },
        {
            label: 'avant la livraison',
            value: 'AVANT_LIVRAISON'
        }
    ];

    const handleOk = () => {
        form.validateFields()
            .then((values) => {
                // Ensure montant is a number
                const parsedValues = {
                    ...values,
                    montant: parseFloat(values.montant),
                    // Transform categories back to the expected format if your backend expects full objects
                    // Otherwise, just passing the IDs/codes might be sufficient
                    categories: categories.filter(cat => values.categories.includes(cat.codeCategorie || cat._id))
                };
                onSubmitCommandeAttestation(parsedValues); // Call the parent handler with form data
                setOrderModalOpen(false); // Close the modal
            })
            .catch((info) => {
                console.log("Validation Failed:", info);
                message.error("Veuillez remplir tous les champs requis correctement.");
            });
    };

    const handleCancel = () => {
        setOrderModalOpen(false);
        form.resetFields(); // Reset form when modal is closed
    };

    return (
        <Modal
            title={
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                    <Title level={3} style={{ textAlign: 'center', marginBottom: 10, color: '#1890ff' }}>
                        {editingProduct ? "Modifier une attestation" : "Ajouter une nouvelle attestation"}
                    </Title>
                </motion.div>
            }
            open={orderModalOpen}
            onOk={handleOk}
            onCancel={handleCancel}
            footer={[
                <Button key="back" onClick={handleCancel}>
                    Annuler
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    onClick={handleOk}
                    // Ant Design form handles validation state, so disabled based on form.isFieldsTouched() and form.getFieldsError()
                    // or just let the validation in handleOk prevent submission
                >
                    {editingProduct ? "Mettre à jour" : "Enregistrer"}
                </Button>,
            ]}
            zIndex={1900}
            centered
            width={700}
            className="order-modal"
        >
            <Form
                form={form} // Bind the form instance
                layout="vertical"
                name="produit_attestation_form"
                // Initial values are handled by useEffect when editing
            >
                {/* Type d'attestation */}
                <Form.Item
                    name="typeAttestation"
                    label="Type d'attestation"
                    rules={[{ required: true, message: "Veuillez entrer le type d'attestation !" }]}
                >
                    <Input placeholder="Type d'attestation" style={{ width: '100%' }} />
                </Form.Item>

                {/* Catégorie */}
                <Form.Item
                    name="categories"
                    label="Catégories"
                    rules={[{ required: true, message: "Veuillez sélectionner au moins une catégorie !" }]}
                >
                    <Select
                        mode="multiple"
                        size="middle"
                        style={{ width: '100%' }}
                        placeholder="Veuillez sélectionner une ou plusieurs catégories"
                    >
                        {categories.map((cat, index) => (
                            <Option key={cat.codeCategorie || cat._id} value={cat.codeCategorie || cat._id}>
                                {cat.nom}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                {/* Coût unitaire */}
                <Form.Item
                    name="montant"
                    label="Coût unitaire"
                    rules={[
                        { required: true, message: "Veuillez entrer le coût unitaire !" },
                        { type: 'number', min: 1, message: "Le montant doit être un nombre positif !" }
                    ]}
                >
                    <InputNumber
                        min={1}
                        style={{ width: '100%' }}
                        placeholder="Entrez le montant"
                        formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        parser={value => value.replace(/\$\s?|(,*)/g, '')}
                    />
                </Form.Item>

                {/* Moyen de règlement */}
                <Form.Item
                    name="moyenReglement"
                    label="Le règlement des commandes de cette attestation se fera "
                    rules={[{ required: true, message: "Veuillez sélectionner un moyen de règlement !" }]}
                >
                    <Select
                        placeholder="Moyen de règlement"
                        style={{ width: '100%' }}
                        options={paymentModality}
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default AddProduitAttestation;